<script setup lang="ts">
import QRCode from 'qrcode';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useDeviceDetection } from '../composables/useDeviceDetection';
import { useSupabaseAuth } from '../composables/useSupabaseAuth';
import { useGameChromeVisibility } from '../composables/useGameChromeVisibility';
import BaseButton from './BaseButton.vue';
import IconButton from './IconButton.vue';
import TitleIcon from '@/assets/title.svg';
import DropdownMenu from './DropdownMenu.vue';
import { useConvexQuery } from 'convex-vue';
import { api } from '../../convex/_generated/api';
import { useScreenOrientation } from '@vueuse/core';
import { useEdgeDragDismiss } from '@/composables/useEdgeDragDismiss';
import { useOutsidePointerDismiss } from '@/composables/useOutsidePointerDismiss';

const router = useRouter();
const appVersion = __APP_BUNDLE_VERSION__;
const { isMobileDevice } = useDeviceDetection();
const { user } = useSupabaseAuth();
const { data: appUser } = useConvexQuery(api.users.getCurrentUser, {});
const canAccessAdmin = computed(() => {
  const role = appUser.value?.role;
  return role === 'admin' || role === 'teacher';
});
const showQR = ref(false);
const qrCodeUrl = ref('');
const showProfileMenu = ref(false);
const mobileMenuOpen = ref(false);
const screenOrientation = useScreenOrientation();
// Mobile-only: the hamburger button hides with the game's other chrome
// (mid-play, idle) so the sheet music gets the full screen height. Desktop's
// header stays put — see useGameChromeVisibility's `reset()`.
const chrome = useGameChromeVisibility();

// Drag-to-dismiss: menu slides in from the left, so only a left-ward drag
// past the threshold closes it — mirrored by AnnotationPane and
// GameAreaSettingsPane for their own edge-anchored panes.
const { dragTranslateX: menuTranslateX, isDragging: isDraggingMenu, handlers: menuDragHandlers } =
  useEdgeDragDismiss(mobileMenuOpen, { anchor: 'left' });

// Close mobile menu when a pointerdown lands outside it and outside its own
// hamburger toggle (excluded so the toggle click isn't double-handled).
useOutsidePointerDismiss(mobileMenuOpen, ['.mobile-menu-container', '.hamburger-button']);

onMounted(async () => {
  try {
    qrCodeUrl.value = await QRCode.toDataURL(window.location.href);
  } catch (err) {
    console.error('Error generating QR code:', err);
  }

  // Add escape key listener to close menus
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      mobileMenuOpen.value = false;
    }
  });
});

const navigateTo = (path: string) => {
  router.push(path);
  showProfileMenu.value = false;
  mobileMenuOpen.value = false;
};

const getInitials = (name?: string, email?: string): string => {
  if (name) {
    return name.split(' ')
      .map(n => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }
  
  if (email) {
    return email.charAt(0).toUpperCase();
  }
  
  return 'U';
};

// Type-safe function to get user name from Supabase user object
interface SupabaseUserLike {
  email?: string;
  user_metadata?: {
    name?: string;
    avatar_url?: string;
  }
}

const getUserDisplayName = (user?: SupabaseUserLike | null): string => {
  if (!user) return 'Account';
  
  if (user.user_metadata?.name) {
    return user.user_metadata.name.split(' ')[0];
  }
  
  if (user.email) {
    return user.email.split('@')[0];
  }
  
  return 'Account';
};
</script>

<template>
  <!-- Desktop Navigation -->
  <header
    v-if="!isMobileDevice"
    class="main-nav-header fixed top-0 left-0 right-0 bg-surface/80 backdrop-blur-lg shadow-sm z-20"
  >
    <nav
      class="mx-auto px-4 sm:px-6 lg:px-8"
      role="navigation"
    >
      <div class="flex items-center h-16 gap-3">
        <!-- Logo -->
        <button 
          class="flex items-center gap-3 shrink-0" 
          aria-label="Go to homepage"
          @click="navigateTo('/')"
        >
          <TitleIcon
            alt="DrumbleBee"
            class="h-8"
          />
        </button>

        <!-- Desktop Nav Links (admin / teacher only) -->
        <nav
          v-if="canAccessAdmin"
          class="flex items-center gap-1 ml-4"
        >
          <a
            href="/"
            class="nav-link relative px-3 py-2 rounded-lg font-medium transition-colors"
            :class="{ 'nav-link-active': $route.path === '/' }"
            @click.prevent="navigateTo('/')"
          >Home</a>
          <a
            href="/admin"
            class="nav-link relative px-3 py-2 rounded-lg font-medium transition-colors"
            :class="{ 'nav-link-active': $route.path.startsWith('/admin') }"
            @click.prevent="navigateTo('/admin')"
          >Admin</a>
        </nav>

        <!-- Right Side Menu -->
        <div class="ml-auto flex items-center gap-4">
          <!-- Auth Buttons -->
          <div class="flex items-center gap-2">
            <!-- User is signed in -->
            <div
              v-if="user"
              class="relative profile-menu-container"
            >
              <DropdownMenu v-model:show="showProfileMenu">
                <template #activator="{ props }">
                  <button
                    v-bind="props"
                    class="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-surface-hover transition-colors profile-menu-button"
                  >
                    <!-- User avatar or initials -->
                    <div 
                      v-if="user.user_metadata?.avatar_url" 
                      class="w-8 h-8 rounded-full overflow-hidden"
                    >
                      <img 
                        :src="user.user_metadata.avatar_url" 
                        alt="Avatar" 
                        class="w-full h-full object-cover" 
                      >
                    </div>
                    <div 
                      v-else 
                      class="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center"
                    >
                      <span class="text-sm font-medium text-primary">
                        {{ getInitials(user.user_metadata?.name, user.email) }}
                      </span>
                    </div>

                    <!-- Display name -->
                    <span class="hidden sm:inline">
                      {{ getUserDisplayName(user) }}
                    </span>

                    <!-- Dropdown icon -->
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      class="h-4 w-4 transition-transform" 
                      :class="{ 'rotate-180': showProfileMenu }"
                      viewBox="0 0 20 20" 
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path 
                        fill-rule="evenodd" 
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" 
                        clip-rule="evenodd" 
                      />
                    </svg>
                  </button>
                </template>

                <template #default>
                  <button
                    class="dropdown-item"
                    role="menuitem"
                    @click="navigateTo('/profile')"
                  >
                    <UIcon
                      name="i-lucide-user"
                      class="w-4.5 h-4.5 shrink-0"
                    />
                    Profile
                  </button>
                  <button
                    class="dropdown-item"
                    role="menuitem"
                    @click="navigateTo('/settings')"
                  >
                    <UIcon
                      name="i-lucide-settings"
                      class="w-4.5 h-4.5 shrink-0"
                    />
                    Settings
                  </button>
                  <hr class="border-white/10 my-1">
                  <button
                    class="dropdown-item text-error"
                    role="menuitem"
                    @click="navigateTo('/auth?logout=true')"
                  >
                    <UIcon
                      name="i-lucide-log-out"
                      class="w-4.5 h-4.5 shrink-0"
                    />
                    Sign Out
                  </button>
                  <hr class="border-white/10 my-1">
                  <p class="px-4 py-2 text-xs text-text-secondary">
                    Version {{ appVersion }}
                  </p>
                </template>
              </DropdownMenu>
            </div>
            
            <!-- User is not signed in -->
            <template v-else>
              <BaseButton 
                color="primary"
                @click="navigateTo('/auth')"
              >
                Sign In
              </BaseButton>
            </template>
          </div>

          <!-- QR Code Button -->
          <div class="relative">
            <IconButton
              variant="text"
              aria-label="Show QR code"
              @mouseenter="showQR = true"
              @mouseleave="showQR = false"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
            </IconButton>

            <!-- QR Code Popup -->
            <Transition name="qr-popup">
              <div
                v-if="showQR"
                class="absolute right-0 mt-2 p-4 w-40 flex flex-col items-center bg-surface rounded-lg shadow-lg z-40"
              >
                <div class="text-center mb-2">
                  <p class="text-sm font-medium">
                    Scan to open on your phone
                  </p>
                </div>
                <img
                  :src="qrCodeUrl"
                  alt="QR Code"
                  class="w-32 h-32"
                >
              </div>
            </Transition>
          </div>
        </div>
      </div>
    </nav>
  </header>

  <!-- Mobile Hamburger Menu Button (without any bar) -->
  <div
    v-if="isMobileDevice"
    class="fixed top-0 mt-4 left-0 ml-4 z-30 mobile-hamburger-wrap"
    :class="{ 'mobile-hamburger-wrap--hidden': !chrome.visible.value }"
  >
    <button 
      class="hamburger-button p-2 rounded-lg bg-surface/80 backdrop-blur-lg hover:bg-surface-hover transition-colors shadow-md"
      :class="{ 'bg-surface-hover': mobileMenuOpen }"
      aria-controls="mobile-menu-container"
      :aria-expanded="mobileMenuOpen"
      aria-label="Toggle menu"
      @click="mobileMenuOpen = !mobileMenuOpen"
    >
      <div class="relative w-6 h-6">
        <!-- Hamburger Icon -->
        <span 
          class="absolute inset-0 flex items-center justify-center transition-opacity"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </span>
      </div>
    </button>
  </div>

  <!-- Mobile Menu Overlay -->
  <Transition name="mobile-menu-backdrop">
    <div 
      v-if="mobileMenuOpen && isMobileDevice" 
      class="fixed inset-0 z-40 bg-black/80"
      @click="mobileMenuOpen = false"
    />
  </Transition>
  
  <!-- Mobile Menu Panel -->
  <Transition name="mobile-menu">
    <div 
      v-if="mobileMenuOpen && isMobileDevice"
      id="mobile-menu-container"
      role="dialog"
      class="fixed left-0 top-0 bottom-0 z-40 w-[75%] max-w-xs"
      :class="{
        'h-[100dvw]': screenOrientation?.orientation.value?.includes('portrait'),
        'h-[100dvh]': !screenOrientation?.orientation.value?.includes('portrait'),
      }"
      aria-modal="true"
    >
      <div 
        class="h-full p-4 flex flex-col bg-background mobile-menu-container"
        :class="{ 'mobile-menu-container--dragging': isDraggingMenu }"
        :style="{ transform: `translateX(${menuTranslateX}px)` }"
        v-on="menuDragHandlers"
      >
        <!-- Mobile Navigation Links -->
        <nav class="flex flex-col space-y-2">
          <a
            href="/"
            class="mobile-nav-link"
            :class="{ 'mobile-nav-link-active': $route.path === '/' }"
            @click.prevent="navigateTo('/')"
          >
            <UIcon
              name="i-lucide-house"
              class="w-5 h-5 shrink-0"
            />
            Home
          </a>
          <a
            v-if="canAccessAdmin"
            href="/admin"
            class="mobile-nav-link"
            :class="{ 'mobile-nav-link-active': $route.path.startsWith('/admin') }"
            @click.prevent="navigateTo('/admin')"
          >
            <UIcon
              name="i-lucide-shield"
              class="w-5 h-5 shrink-0"
            />
            Admin
          </a>
        </nav>

        <!-- Bottom actions -->
        <div class="mt-auto pt-4 border-t border-white/10">
          <div
            v-if="user"
            class="flex items-center mb-2"
          >
            <div 
              class="flex items-center gap-3 w-full cursor-pointer hover:bg-surface-hover p-2 rounded-lg transition-colors"
              @click="navigateTo('/profile')"
            >
              <div 
                v-if="user.user_metadata?.avatar_url" 
                class="w-10 h-10 rounded-full overflow-hidden"
              >
                <img 
                  :src="user.user_metadata.avatar_url" 
                  alt="Avatar" 
                  class="w-full h-full object-cover" 
                >
              </div>
              <div 
                v-else 
                class="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center"
              >
                <span class="text-sm font-medium text-primary">
                  {{ getInitials(user.user_metadata?.name, user.email) }}
                </span>
              </div>
              <div>
                <div class="font-medium">
                  {{ getUserDisplayName(user) }}
                </div>
                <div class="text-sm text-text-secondary">
                  {{ user.email }}
                </div>
              </div>
            </div>
          </div>
          <BaseButton
            v-if="user"
            color="surface"
            class="w-full justify-center mb-2"
            @click="navigateTo('/settings')"
          >
            <template #icon>
              <UIcon
                name="i-lucide-settings"
                class="w-5 h-5 shrink-0"
              />
            </template>
            Settings
          </BaseButton>

          <!-- Sign In/Out Button -->
          <BaseButton
            v-if="!user"
            color="primary"
            class="w-full justify-center"
            @click="navigateTo('/auth')"
          >
            <template #icon>
              <UIcon
                name="i-lucide-log-in"
                class="w-5 h-5 shrink-0"
              />
            </template>
            Sign In
          </BaseButton>
          <BaseButton
            v-else
            color="error"
            class="w-full justify-center"
            @click="navigateTo('/auth?logout=true')"
          >
            <template #icon>
              <UIcon
                name="i-lucide-log-out"
                class="w-5 h-5 shrink-0"
              />
            </template>
            Sign Out
          </BaseButton>

          <p class="mt-3 text-center text-xs text-text-secondary">
            Version {{ appVersion }}
          </p>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* QR Code Animation */
.qr-popup-enter-active,
.qr-popup-leave-active {
  transition: all 0.2s ease;
}

.qr-popup-enter-from,
.qr-popup-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* Profile Dropdown Animation */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Profile Dropdown Items */
.dropdown-item {
  width: 100%;
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: background-color 0.2s;
}

.dropdown-item:hover {
  background-color: var(--color-surface-hover);
}

/* Mobile Menu Panel Animation */
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
  transform: translateX(-100%);
}

/* Mobile Menu Backdrop Animation */
.mobile-menu-backdrop-enter-active,
.mobile-menu-backdrop-leave-active {
  transition: opacity 0.3s ease;
}

.mobile-menu-backdrop-enter-from,
.mobile-menu-backdrop-leave-to {
  opacity: 0;
}

/* Desktop nav link styles */
.nav-link::after {
  content: '';
  position: absolute;
  height: 2px;
  background-color: var(--color-text-primary);
  left: 0.75rem; /* equivalent to left-3 */
  right: 0.75rem; /* equivalent to right-3 */
  bottom: 0;
  transform: scaleX(0);
  transition: transform 0.2s;
  transform-origin: center;
}

.nav-link:hover {
  background-color: var(--color-surface-hover);
  color: var(--color-text-primary);
}

.nav-link:hover::after,
.nav-link-active::after {
  transform: scaleX(1);
}

.nav-link-active {
  color: var(--color-text-primary);
}

/* Mobile Navigation Links */
.mobile-nav-link {
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  font-weight: 500;
  font-size: 1.125rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: background-color 0.2s, color 0.2s;
}

.mobile-nav-link:hover {
  background-color: var(--color-surface-hover);
}

.mobile-nav-link-active {
  background: linear-gradient(to right, color-mix(in srgb, var(--color-primary) 50%, transparent), transparent);
  color: var(--color-text-primary);
}

/* Mobile menu container */
.mobile-menu-container {
  will-change: transform;
  transition: transform 0.3s ease;
}

/* No transition while actively dragging, so the menu tracks the finger with
   zero lag — mirrors AnnotationPane / GameAreaSettingsPane's `--dragging`. */
.mobile-menu-container--dragging {
  transition: none;
}

/* Desktop header now always stays visible — no auto-hide transition needed. */

/* Auto-hides with the game's other chrome (mid-play, idle) — see
   useGameChromeVisibility. Slides up rather than just fading, so it can't be
   clicked through while transitioning out. */
.mobile-hamburger-wrap {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.mobile-hamburger-wrap--hidden {
  opacity: 0;
  transform: translateY(-100%);
  pointer-events: none;
}
</style> 
